import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, SERVICE_OPTIONS, waLink, mailLink, MAIL_HREF, EMAIL } from "@/lib/site";
import { Reveal, Overline } from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";

const EMPTY = { name: "", phone: "", email: "", service: "", material: "", quantity: "", message: "", contact_via: "whatsapp" };

const summary = (f) =>
  [
    "Hello TSR Industry, I'd like a quotation.",
    `Name: ${f.name}`, `Phone: ${f.phone}`, f.email && `Email: ${f.email}`,
    `Service: ${f.service}`, f.material && `Material: ${f.material}`, f.quantity && `Quantity / size: ${f.quantity}`,
    `Details: ${f.message}`,
  ].filter(Boolean).join("\n");

const fieldCls = "h-12 bg-white/80 border-slate-300 focus-visible:ring-[#c9982f] rounded-sm";

export const QuoteForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    const onQuote = (e) => {
      if (e.detail) setF((s) => ({ ...s, service: e.detail }));
    };
    window.addEventListener("tsr:quote", onQuote);
    return () => window.removeEventListener("tsr:quote", onQuote);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.service) return toast.error("Please choose a service.");
    const text = summary(f);
    // Open the window synchronously so mobile browsers don't block it as a popup.
    const win = f.contact_via === "whatsapp" ? window.open("", "_blank") : null;
    setBusy(true);
    try {
      await axios.post(`${API}/quotes`, { ...f, email: f.email || null, material: f.material || null, quantity: f.quantity || null });
      toast.success("Quotation request received. Opening your message to TSR Industry…");
    } catch (err) {
      toast.message("Couldn't save the request online — your message will still open so you can send it directly.");
    } finally {
      setBusy(false);
    }
    if (f.contact_via === "whatsapp") {
      if (win) win.location.href = waLink(text);
      else window.location.href = waLink(text);
    } else {
      window.location.href = mailLink(`Quotation request - ${f.service}`, text);
    }
    setF(EMPTY);
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-32 blueprint">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <Overline>Request a quotation</Overline>
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.98]">
            The <span className="text-gold-grad">Cut Sheet.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-700 leading-relaxed max-w-md">
            Tell us what you need. We save your request and open WhatsApp or email with every detail filled in — just press send. Attach drawings or photos in that chat.
          </p>
          <div className="mt-10 space-y-3 max-w-md">
            <a data-testid="quote-side-whatsapp" href={waLink()} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between gap-4 p-5 bg-white/80 border border-slate-300 hover:border-[#25D366] transition-colors">
              <span className="flex items-center gap-3 font-semibold"><WhatsAppIcon className="h-5 w-5 text-[#25D366]" /> Prefer to chat? WhatsApp us</span>
              <span aria-hidden="true">→</span>
            </a>
            <a data-testid="quote-side-email" href={MAIL_HREF} className="flex items-center justify-between gap-4 p-5 bg-white/80 border border-slate-300 hover:border-[#c9982f] transition-colors">
              <span className="font-semibold break-all">{EMAIL}</span><span aria-hidden="true">→</span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form data-testid="quote-form" onSubmit={submit} className="chamfer bg-white/90 backdrop-blur border border-slate-200 p-6 sm:p-10 shadow-[0_40px_80px_-40px_rgba(14,17,22,0.35)]">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label htmlFor="q-name">Full name *</Label><Input id="q-name" data-testid="quote-name-input" required minLength={2} value={f.name} onChange={set("name")} className={`${fieldCls} mt-2`} /></div>
              <div><Label htmlFor="q-phone">Phone / WhatsApp *</Label><Input id="q-phone" data-testid="quote-phone-input" type="tel" required minLength={7} value={f.phone} onChange={set("phone")} className={`${fieldCls} mt-2`} /></div>
              <div><Label htmlFor="q-email">Email</Label><Input id="q-email" data-testid="quote-email-input" type="email" value={f.email} onChange={set("email")} className={`${fieldCls} mt-2`} /></div>
              <div>
                <Label>Service *</Label>
                <Select value={f.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="quote-service-select" className={`${fieldCls} mt-2`}><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label htmlFor="q-material">Material</Label><Input id="q-material" data-testid="quote-material-input" placeholder="e.g. MS sheet 3mm, teak" value={f.material} onChange={set("material")} className={`${fieldCls} mt-2`} /></div>
              <div><Label htmlFor="q-qty">Quantity / size</Label><Input id="q-qty" data-testid="quote-quantity-input" placeholder="e.g. 20 pcs, 4ft x 2ft" value={f.quantity} onChange={set("quantity")} className={`${fieldCls} mt-2`} /></div>
              <div className="sm:col-span-2"><Label htmlFor="q-msg">Project details *</Label><Textarea id="q-msg" data-testid="quote-message-input" required minLength={5} rows={5} value={f.message} onChange={set("message")} className="mt-2 bg-white/80 border-slate-300 rounded-sm focus-visible:ring-[#c9982f]" /></div>
            </div>
            <fieldset className="mt-6">
              <legend className="text-sm font-medium">Send my request via</legend>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {[["whatsapp", "WhatsApp"], ["email", "Email"]].map(([v, l]) => (
                  <button type="button" key={v} data-testid={`quote-via-${v}`} onClick={() => set("contact_via")(v)}
                    className={`h-12 border font-semibold transition-colors ${f.contact_via === v ? "bg-[#0e1116] text-white border-[#0e1116]" : "bg-white border-slate-300 hover:border-slate-500"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </fieldset>
            <button type="submit" data-testid="quote-submit-btn" disabled={busy}
              className="mt-8 w-full h-14 rounded-full bg-gold-grad text-[#0e1116] font-bold text-base inline-flex items-center justify-center gap-2 hover:brightness-105 disabled:opacity-60 transition-[filter,opacity]">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Send quotation request
            </button>
            <p className="mt-4 text-xs text-slate-600">Your details are used only to reply to this enquiry.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
